"use client";

import type { SearchSuggestion } from "@ui/components/core/SearchInput";

import { SearchInput } from "@ui/components/core/SearchInput";
import PageSection from "@ui/components/composers/PageSection";
import { useTranslations } from "next-intl";
import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";

import { useSfxCategorySearch, type CategorySearchItem } from "./hooks/useSfxCategorySearch";

interface SfxSearchSectionProps {
  searchSuggestions: SearchSuggestion[];
  locale: string;
  onCategorySelect?: (slug: string) => void;
  onCategorySearch?: (
    query: string,
    signal: AbortSignal,
  ) => Promise<{ results: { id: string; slug: string; title: string; breadcrumbName?: string | null }[] }>;
  onSearchSubmit?: (query: string) => void;
}

export function SfxSearchSection({
  searchSuggestions,
  locale,
  onCategorySelect,
  onCategorySearch,
  onSearchSubmit,
}: SfxSearchSectionProps) {
  const t = useTranslations("ClientKeys.SoundEffectCategories.search");
  const { query, setQuery, searchItems, isSearching, clearSearch } = useSfxCategorySearch({
    onCategorySearch,
    locale,
  });

  return (
    <PageSection withGap={false} topPadding='small' bottomPadding='small' horizontalPadding='none'>
      <form
        onSubmit={e => {
          e.preventDefault();
          onSearchSubmit?.(query);
        }}
      >
        <SearchInput<CategorySearchItem>
          aria-label={t("ariaLabel")}
          compact
          inputValue={query}
          onInputChange={setQuery}
          onClear={() => {
            clearSearch();
            onSearchSubmit?.("");
          }}
          placeholder={t("placeholder")}
          items={searchItems}
          selectionMode='none'
          onItemAction={item => {
            captureEvent(
              EventName.mw_sfx_search_result_clicked,
              {
                query,
                result_slug: item.slug,
                result_position: searchItems.findIndex(s => s.id === item.id),
                total_results: searchItems.length,
              },
              { transport: "sendBeacon" },
            );
            onCategorySelect?.(item.slug);
          }}
          isLoading={isSearching}
          loadingMessage={t("loading")}
          emptyMessage={t("no_results")}
          suggestions={searchSuggestions}
          suggestionsLabel={t("suggestionsLabel")}
        />
      </form>
    </PageSection>
  );
}
